import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl,
  InputLabel, MenuItem, Select, Stack, TextField,
} from '@mui/material';
import * as api from '../plannerApi';
import * as T from '../types';
import { ItemChoice, ItemPicker, useLoadInto } from '../common';
import { SECTIONS, UNITS, parseNumber, quantity } from '../labels';

/** "Tomate de Mãe: 2,5 kg em casa · riscado da lista", as Android's GiftPage. */
function giftMessage(result: T.ReceiveGiftResult, givenBy: string): string {
  const parts = [`${result.item.name} de ${givenBy}: ${quantity(result.item.quantity, result.item.unit) || '—'} em casa`];
  if (result.crossedOff > 0) parts.push('riscado da lista');
  else if (result.listReduced) parts.push('menos na lista');
  return parts.join(' · ');
}

/**
 * "Recebi de oferta": something someone gave (from their garden) goes into the stock and off the list, with who gave it.
 * With `item` it is for that item; otherwise the item is chosen (or created) here.
 */
export const GiftDialog: React.FC<{
  open: boolean;
  item?: T.StockItem | null;
  onClose: () => void;
  onDone: (result: T.ReceiveGiftResult, message: string) => void;
}> = ({ open, item, onClose, onDone }) => {
  const [choice, setChoice] = useState<ItemChoice | null>(null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<T.MeasureUnit>('Kilogram');
  const [section, setSection] = useState<T.StoreSection>('FruitAndVegetables');
  const [givenBy, setGivenBy] = useState('');
  const [givers, setGivers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChoice(null);
    setAmount('');
    setUnit(item?.unit ?? 'Kilogram');
    setSection('FruitAndVegetables');
    setGivenBy('');
    setError(null);
    setBusy(false);
  }, [open, item]);
  useLoadInto(open ? () => api.getGiftGivers().catch(() => [] as string[]) : null, setGivers, [open, item]);

  const choose = (next: ItemChoice | null) => {
    setChoice(next);
    if (next && 'item' in next) setUnit(next.item.defaultUnit);
  };

  const submit = async () => {
    if (!item && !choice) { setError('Escolhe ou cria o artigo.'); return; }
    const value = parseNumber(amount);
    if (value === null || value <= 0) { setError('Diz quanto te deram.'); return; }
    const who = givenBy.trim();
    if (!who) { setError('Diz quem ofereceu.'); return; }
    const isNew = !item && choice !== null && 'newName' in choice;
    setBusy(true);
    try {
      const result = await api.receiveGift({
        catalogItemPublicId: item?.catalogItemPublicId ?? (choice && 'item' in choice ? choice.item.publicId : null),
        newItemName: isNew && choice && 'newName' in choice ? choice.newName : null,
        quantity: value,
        unit,
        givenBy: who,
        newItemSection: isNew ? section : null,
      });
      onDone(result, giftMessage(result, who));
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  const isNew = !item && choice !== null && 'newName' in choice;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={e => { e.preventDefault(); submit(); }}>
        <DialogTitle>{item ? `Recebi ${item.name} de oferta` : 'Recebi de oferta'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>O que te deram entra em casa e sai da lista de compras, sem despesa.</DialogContentText>
          <Stack spacing={2}>
            {!item && <ItemPicker value={choice} onChange={choose} kind="Food" autoFocus />}
            {isNew && (
              <FormControl fullWidth>
                <InputLabel id="gift-section">Secção do supermercado</InputLabel>
                <Select labelId="gift-section" label="Secção do supermercado" value={section} onChange={e => setSection(e.target.value as T.StoreSection)}>
                  {SECTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.text}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Quantidade" value={amount} onChange={e => setAmount(e.target.value)} autoFocus={!!item}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }} sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id="gift-unit">Unidade</InputLabel>
                <Select labelId="gift-unit" label="Unidade" value={unit} onChange={e => setUnit(e.target.value as T.MeasureUnit)}>
                  {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.text}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Autocomplete
              freeSolo
              options={givers}
              inputValue={givenBy}
              onInputChange={(_, value) => setGivenBy(value)}
              renderInput={params => (
                <TextField {...params} label="Quem ofereceu" placeholder="Ex.: Mãe, vizinho Zé" />
              )}
            />
          </Stack>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={busy}>Guardar</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
